







































import { ref, computed, useContext, useRoute, defineComponent, useFetch, onMounted } from '@nuxtjs/composition-api';
import { isNumber, merge } from 'lodash-es';
import base64 from 'base-64';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { SfLoader } from '@storefront-ui/vue';
import SfBreadcrumbs from '~/components/SfBreadcrumbs.vue';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import { useApi, useUiNotification } from '~/composables';
import type { Product } from '~/modules/catalog/product/types';
import type {
  ProductDetailsQuery,
  FreeGiftProductQuery,
  FreeGiftProducts,
  GetProductBreadcrumb,
} from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import getCustomProductOptionsGql from '~/customQueries/catalog/product/queries/getCustomProductOptions.gql';
import TrustedShopAllReviews from '~/modules/catalog/product/components/TrustedShopAllReviews.vue';
import FfRecommendation from '~/components/FactFinderComp/FfRecommendation.vue';
import { getFreeGiftProductByProductSku } from '~/modules/catalog/product/queries/getFreeGiftProductByProductSku.gql';
import { useCategory } from '~/modules/catalog/category/composables/useCategory';
import { useUser } from '~/modules/customer/composables/useUser';

import { useUrlData } from '~/stores/urldata';

import useShippingCost from '~/composables/useShippingCost';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () =>
      import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue'),
    UpsellProducts: () => import('~/modules/catalog/product/components/UpsellProducts.vue'),
    FreeProducts: () => import('~/modules/catalog/product/components/FreeProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
    TrustedShopAllReviews,
    FfRecommendation,
  },
  transition: 'fade',
  setup() {
    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { localePath } = useContext();
    const route = useRoute();
    const { getProductDetails, loading, getProductBreadcrumbs } = useProduct();
    const { error: nuxtError } = useContext();
    const { send: sendNotification } = useUiNotification();
    const urlData = useUrlData();
    const { getCategoriesByFilter } = useCategory();
    const { user, load: loadUser, isAuthenticated } = useUser();
    const resolvedUrl = urlData.resolverUrl;

    const { load: loadShippingCost, shippingCost } = useShippingCost();

    // @ts-ignore
    const id = resolvedUrl?.sku || '';
    const freeGiftsOfProduct = ref<FreeGiftProducts | {}>(null);
    const giftAvailableCheck = ref<string | null>(null);

    const factFinderProduId = ref(null);

    const productBreadcrumbs = ref<GetProductBreadcrumb | null>(null);

    const breadcrumbs = computed(() => {
      const productCategories = product.value?.categories ?? [];
      if (productCategories.length == 0) {
        return [];
      }
      const highestValueItem = productCategories.reduce((prev, current) => {
        return prev.breadcrumbs_priority > current.breadcrumbs_priority ? prev : current;
      });
      if (productBreadcrumbs && productBreadcrumbs.value?.products?.items[0]?.product_top_category) {
        const result =
          productBreadcrumbs.value?.products?.items[0]?.breadcrumbs?.map((breadcrumb) => ({
            text: breadcrumb.category_name,
            link: breadcrumb.category_url_path + '.html',
          })) || [];
        result.push({
          text: product.value.name,
          link: '',
        });

        return result;
      } else {
        return getBreadcrumbs(product.value, Array.isArray(productCategories) ? highestValueItem : null).map(
          (breadcrumb) => {
            let newLink: any;
            if (product && product.value && product.value.name && product.value.name === breadcrumb.text) {
              newLink = '';
            } else {
              newLink = localePath(breadcrumb.link);
            }
            return { ...breadcrumb, link: newLink };
          },
        );
      }
    });

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: id,
        },
      },
      search: ' ',
      configurations: Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map((config) => config[1]) as string[],
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const baseProdDataLoading = ref(false);
    const extProdDataLoading = ref(false);
    const isAllDataFetched = ref(false);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      try {
        baseProdDataLoading.value = true;
        const result = await getProductDetails({
          ...searchQuery,
        });

        if (result && result.items.length > 0) {
          let resultProd = result.items[0];

          let catIds = [];
          if (isNumber(resultProd.product_top_category)) {
            catIds.push(resultProd.product_top_category);
          }
          if (isNumber(resultProd.manufacturer_main_category)) {
            catIds.push(resultProd.manufacturer_main_category);
          }

          if (catIds.length > 0) {
            let categoryIdsData = await getCategoriesByFilter(catIds);
            if (categoryIdsData && categoryIdsData.length > 0) {
              if (resultProd.product_top_category) {
                // @ts-ignore
                let ptcData = categoryIdsData.find((val) => val.id == resultProd.product_top_category);
                if (ptcData) {
                  resultProd.product_top_category = { ...ptcData };
                }
              }

              if (resultProd.manufacturer_main_category) {
                // @ts-ignore
                let mmcData = categoryIdsData.find((val) => val.id == resultProd.manufacturer_main_category);
                if (mmcData) {
                  resultProd.manufacturer_main_category = { ...mmcData };
                }
              }
            }
          }

          product.value = merge({}, product.value, (resultProd as Product) ?? null);
        }
      } catch (e) {
      } finally {
        baseProdDataLoading.value = false;
      }
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      try {
        let tmp_manufacturer_main_category = product.value.manufacturer_main_category;
        let tmp_product_top_category = product.value.product_top_category;
        extProdDataLoading.value = true;
        if (product.value?.sku) {
          const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);
          const { data: optionData } = await query(getCustomProductOptionsGql, { sku: product.value?.sku });

          // @ts-ignore
          if (optionData?.products?.items?.length) product.value.options = optionData.products?.items?.[0].options;
          // const { data:data2 } = await query<ProductDetailsQuery>(getProductCustomAttributes, searchQuery);
          // product.value = merge({}, product.value, data.products?.items?.[0] as Product, data2?.products?.items?.[0] as Product);
          product.value = merge({}, product.value, data.products?.items?.[0] as Product);

          // clean up media gallery
          if (product.value?.configurable_product_options_selection && searchQuery?.configurations?.length > 0) {
            // @ts-ignore
            if (data.products?.items?.[0].configurable_product_options_selection?.media_gallery?.length > 0) {
              // @ts-ignore
              // prettier-ignore
              product.value.configurable_product_options_selection.media_gallery = data.products.items[0].configurable_product_options_selection.media_gallery;
            }
          }

          // Factfinder ID
          if (product && product.value) {
            factFinderProduId.value = product.value?.id || base64.decode(product.value?.uid) || null;
          }

          if (product.value?.__typename == 'ConfigurableProduct') {
            // let isProdAvailable = product.value?.configurable_product_options_selection?.options_available_for_selection?.length > 0 || false;
            let isProdAvailable = product.value?.configurable_product_options_selection?.variant || false;
            if (!isProdAvailable && isAllDataFetched.value) {
              sendNotification({
                id: Symbol('product_removed'),
                message: 'Produkt nicht lieferbar.',
                type: 'danger',
                icon: 'error',
                persist: false,
              });
            }
          }

          // for shipping data
          if (!shippingCost.value) {
            await loadShippingCost();
          }
          product.value.shipping_cost_data = shippingCost.value;
          product.value.manufacturer_main_category = tmp_manufacturer_main_category;
          product.value.product_top_category = tmp_product_top_category;
        }
      } catch (error) {
        console.log('ERROR:', error);
      } finally {
        extProdDataLoading.value = false;
      }
    };

    useFetch(async () => {
      if (!shippingCost.value) {
        await loadShippingCost();
      }

      await fetchAllData();

      isAllDataFetched.value = true;
      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${id}`,
        },
      ];

      const productTags = [
        {
          prefix: CacheTagPrefix.Product,
          value: product.value.uid,
        },
      ];

      if (!isAuthenticated.value) {
        //eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        addTags([...tags, ...productTags]);
      }

      getProductBreadcrumbs(product.value?.sku).then((data) => {
        productBreadcrumbs.value = data;
      });
      if (user.value === null) {
        await loadUser();
      }
    });

    const fetchAllData = async (searchQuery = getBaseSearchQuery()) => {
      await fetchProductBaseData(searchQuery);
      await fetchProductExtendedData(searchQuery);
    };

    onMounted(async () => {
      let searchQueryForGiftProduct = {
        sku: resolvedUrl?.sku,
      };
      const { data } = await query<FreeGiftProductQuery>(getFreeGiftProductByProductSku, searchQueryForGiftProduct);
      freeGiftsOfProduct.value = data ? data : null;
      giftAvailableCheck.value =
        data &&
        data.mpFreeGiftsByProductSku &&
        data.mpFreeGiftsByProductSku.length > 0 &&
        data.mpFreeGiftsByProductSku[0].notice
          ? data.mpFreeGiftsByProductSku[0].notice
          : '';
    });

    return {
      baseProdDataLoading,
      extProdDataLoading,
      factFinderProduId,
      renderer,
      loading,
      breadcrumbs,
      product,
      // fetchProduct: fetchAllData,
      fetchProduct: fetchProductExtendedData,
      freeGiftsOfProduct,
      giftAvailableCheck,
    };
  },

  methods: {
    goBack() {
      // this.$router.go(-1);
      window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/');
    },

    recmWrap() {
      /* IF THERE IS NO RECOMENDED WRAPPER HIDE ELEMENT - TO PREVENT SPACING ISSUES */
      const recmdWrap = document.querySelectorAll('.recmdWrap .ffw-hidden:not(.row)');
      if (recmdWrap.length) {
        recmdWrap.forEach(function (item) {
          if (item.classList.contains('ffw-hidden')) {
            const thisParent = item.parentElement;
            const thisParents = thisParent.parentElement;

            thisParents.classList.add('is-hidden');
          }
        });
      }
    },
  },

  mounted() {
    this.recmWrap();
  },
});
